import { useState, useCallback, useMemo, useRef, useEffect } from 'react';

/**
 * REFACTORED useEnabledSensors - Maintains all original functionality while eliminating race conditions
 *
 * Key improvements:
 * - Eliminated complex batching that caused timing issues
 * - Fixed state update coordination to prevent conflicts
 * - Maintains all original features: loading states, duplicate ID handling, etc.
 * - Predictable state updates without complex timing dependencies
 */

// Helper function to ensure unique section IDs (maintains original functionality)
const processSensorMapForUniqueIds = (
  initialSensorMap: Record<string, Record<string, boolean>>
): Record<string, Record<string, boolean>> => {
  const sectionIds = Object.keys(initialSensorMap);
  const duplicates = sectionIds.filter((id, index) => sectionIds.indexOf(id) !== index);

  if (duplicates.length > 0) {

    const processed: Record<string, Record<string, boolean>> = {};
    const idCounter: Record<string, number> = {};

    Object.entries(initialSensorMap).forEach(([sectionId, sensors]) => {
      idCounter[sectionId] = (idCounter[sectionId] || 0) + 1;
      const uniqueId = idCounter[sectionId] > 1 ? `${sectionId}_${idCounter[sectionId]}` : sectionId;

      if (uniqueId !== sectionId) {
      }

      processed[uniqueId] = sensors;
    });

    return processed;
  }

  return initialSensorMap;
};

export const useEnabledSensors = (initialSensorMap: Record<string, Record<string, boolean>>) => {
  // Process initial sensor map to handle potential duplicate IDs
  const processedSensorMap = useMemo(() => {
    const processed = processSensorMapForUniqueIds(initialSensorMap);
    return processed;
  }, [initialSensorMap]);

  const [enabledSensors, setEnabledSensors] = useState<Record<string, Record<string, boolean>>>(processedSensorMap);
  const [loadingSensors, setLoadingSensors] = useState<Record<string, Record<string, boolean>>>({});
  const initialSensorMapRef = useRef(processedSensorMap);

  // FIXED: Removed complex batching that caused timing issues
  // State updates are now immediate and predictable

  // Update initial sensor map when it changes (component ID change)
  useEffect(() => {
    const currentKey = JSON.stringify(initialSensorMapRef.current);
    const newKey = JSON.stringify(processedSensorMap);

    if (currentKey !== newKey) {

      // FIXED: Immediate state update without complex coordination
      setEnabledSensors(processedSensorMap);
      setLoadingSensors({});
      initialSensorMapRef.current = processedSensorMap;
    }
  }, [processedSensorMap]);

  // FIXED: Toggle sensor with immediate state update (no batching delays)
  const toggleSensor = useCallback((sectionKey: string, sensorName: string) => {

    setEnabledSensors((prev) => {
      const sectionSensors = prev[sectionKey] || {};
      const currentValue = sectionSensors[sensorName];
      const newValue = !currentValue;


      const newSectionSensors = {
        ...sectionSensors,
        [sensorName]: newValue,
      };

      const newState = {
        ...prev,
        [sectionKey]: newSectionSensors,
      };

      return newState;
    });
  }, []);

  // FIXED: Enable sensor with immediate state update (no batching delays)
  const enableSensor = useCallback((sectionKey: string, sensorName: string) => {

    setEnabledSensors((prev) => {
      const currentValue = prev[sectionKey]?.[sensorName];

      // Only update if the value is actually changing
      if (currentValue === true) {
        return prev;
      }

      const newState = {
        ...prev,
        [sectionKey]: {
          ...prev[sectionKey],
          [sensorName]: true,
        },
      };


      return newState;
    });
  }, []);

  // Disable sensor (immediate state update)
  const disableSensor = useCallback((sectionKey: string, sensorName: string) => {

    setEnabledSensors((prev) => ({
      ...prev,
      [sectionKey]: {
        ...prev[sectionKey],
        [sensorName]: false,
      },
    }));
  }, []);

  // Clear sensors (immediate state update)
  const clearSensors = useCallback((sectionKey: string) => {

    setEnabledSensors((prev) => {
      const sectionSensors = prev[sectionKey] || {};
      const clearedSensors = Object.keys(sectionSensors).reduce((acc, key) => {
        acc[key] = false;
        return acc;
      }, {} as Record<string, boolean>);

      return {
        ...prev,
        [sectionKey]: clearedSensors,
      };
    });
  }, []);

  // Reset sensors (immediate state update)
  const resetSensors = useCallback((sectionKey?: string) => {
    if (sectionKey) {

      const initialSectionSensors = initialSensorMapRef.current[sectionKey] || {};
      const sensorNames = Object.keys(initialSectionSensors);
      
      // Check if this appears to be an alarm section
      const hasAlarmPattern = sensorNames.some(name => 
        name.includes('Active') || name.includes('Occurred') || name.includes('@')
      );
      if (hasAlarmPattern) {
      }
      
      setEnabledSensors((prev) => {
        const newState = {
          ...prev,
          [sectionKey]: { ...initialSectionSensors },
        };
        return newState;
      });
    } else {
      setEnabledSensors({ ...initialSensorMapRef.current });
    }
  }, []);

  // Force reset (used for component changes) - immediate update
  const forceResetToInitial = useCallback(() => {
    setEnabledSensors({ ...processedSensorMap });
    setLoadingSensors({});
  }, [processedSensorMap]);

  // FIXED: Loading sensor state management (maintains original functionality)
  const setLoadingSensor = useCallback((sectionKey: string, sensorName: string, isLoading: boolean) => {

    setLoadingSensors((prev) => {
      const sectionLoading = prev[sectionKey] || {};

      if (isLoading) {
        return {
          ...prev,
          [sectionKey]: {
            ...sectionLoading,
            [sensorName]: true,
          },
        };
      } else {
        // Remove the sensor from loading state
        const { [sensorName]: removed, ...restSectionLoading } = sectionLoading;
        return {
          ...prev,
          [sectionKey]: restSectionLoading,
        };
      }
    });
  }, []);

  const isSensorLoading = useCallback(
    (sectionKey: string, sensorName: string): boolean => {
      return loadingSensors[sectionKey]?.[sensorName] || false;
    },
    [loadingSensors]
  );

  // FIXED: Removed complex debounced logging that could cause issues
  // Simple immediate logging for debugging
  useEffect(() => {

    // Log any potential ID conflicts
    const sectionIds = Object.keys(enabledSensors);
    const duplicateIds = sectionIds.filter((id, index) => sectionIds.indexOf(id) !== index);
    if (duplicateIds.length > 0) {
    }
  }, [enabledSensors]);

  // FIXED: Simplified cleanup without complex timeout management
  useEffect(() => {
    return () => {
      // Simple cleanup - no complex timeout coordination needed
    };
  }, []);

  const memoizedValues = useMemo(
    () => ({
      enabledSensors,
      loadingSensors,
      toggleSensor,
      enableSensor,
      disableSensor,
      clearSensors,
      resetSensors,
      forceResetToInitial,
      setLoadingSensor,
      isSensorLoading,
      processedSensorMap,
      originalSensorMap: initialSensorMap,
    }),
    [
      enabledSensors,
      loadingSensors,
      toggleSensor,
      enableSensor,
      disableSensor,
      clearSensors,
      resetSensors,
      forceResetToInitial,
      setLoadingSensor,
      isSensorLoading,
      processedSensorMap,
      initialSensorMap,
    ]
  );

  return memoizedValues;
};
